import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CUSTOM_CATEGORY_PALETTE, type CategoryInput, type ExpenseCostType } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, deleteCategory, fetchCategories, saveCategory, type CategoryRow } from "../../expenses/services/expensesService";
import { Chip, ErrorBox, Field, Notice, Page, Spinner } from "../../expenses/components/ui";
import { lang } from "../../expenses/lib/format";
import { ui } from "../../expenses/theme";

// Categorías de Finanzas internas (#1255). Las del sistema se pueden renombrar,
// recolorear y cambiar entre gasto fijo y variable; además se crean categorías
// propias. «Borrar» una categoría con movimientos la ARCHIVA: deja de ofrecerse
// al capturar, pero los gastos y presupuestos pasados la conservan. Las que el
// sistema usa con significado propio no se archivan.

const emptyForm = { id: "", name: "", nameEn: "", color: CUSTOM_CATEGORY_PALETTE[0] as string, costType: "variable" as ExpenseCostType };

export default function CategoriesSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const tc = (k: string, o?: Record<string, unknown>) => t(`financeSettings.categories.${k}`, o);
  const [rows, setRows] = useState<CategoryRow[] | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetchCategories(true);
      setRows(r.categories);
      setCanEdit(r.canEdit);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("financeSettings.errors.load"));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true);
    setNotice(null);
    try {
      const message = await fn();
      setNotice(message);
      setError(null);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || t("financeSettings.errors.save"));
    } finally {
      setBusy(false);
    }
  };

  const submit = () =>
    run(async () => {
      const payload: CategoryInput = { name: form.name, nameEn: form.nameEn || null, color: form.color, costType: form.costType };
      await saveCategory(payload, form.id || undefined);
      const done = tc(form.id ? "saved" : "created", { name: form.name });
      setForm({ ...emptyForm, color: CUSTOM_CATEGORY_PALETTE[((rows?.length || 0) + 1) % CUSTOM_CATEGORY_PALETTE.length] });
      return done;
    });

  const edit = (r: CategoryRow) => {
    setForm({ id: r.id, name: r.label.es, nameEn: r.label.en === r.label.es ? "" : r.label.en, color: r.color, costType: r.costType });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const setArchived = (r: CategoryRow, archived: boolean) =>
    run(async () => {
      await saveCategory({ archived }, r.id);
      return tc(archived ? "archived" : "restored", { name: r.label[lang(i18n.language)] });
    });

  const remove = (r: CategoryRow) =>
    run(async () => {
      const res = await deleteCategory(r.id);
      return tc(res.result === "deleted" ? "deleted" : "archivedInstead", { name: r.label[lang(i18n.language)] });
    });

  const usageText = (r: CategoryRow) => {
    const u = r.usage;
    if (!u || u.expenses + u.budgets + u.platforms === 0) return tc("unused");
    return [u.expenses && tc("usageExpenses", { count: u.expenses }), u.budgets && tc("usageBudgets", { count: u.budgets }), u.platforms && tc("usagePlatforms", { count: u.platforms })].filter(Boolean).join(" · ");
  };
  const inUse = (r: CategoryRow) => !!r.usage && r.usage.expenses + r.usage.budgets + r.usage.platforms > 0;

  return (
    <Page title={tc("title")} subtitle={tc("subtitle")}>
      <ErrorBox message={error} />
      {notice && <Notice tone="ok">{notice}</Notice>}
      {rows && !canEdit && <Notice tone="info">{t("financeSettings.readOnly")}</Notice>}

      {canEdit && (
        <div ref={formRef} className={c.card} data-testid="category-form">
          <h2 className={`text-sm font-semibold ${c.text}`}>{form.id ? tc("editTitle") : tc("formTitle")}</h2>
          <p className={`mb-3 text-xs ${c.muted}`}>{tc("formHint")}</p>
          <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-5">
            <Field label={tc("name")}>
              <input className={c.input} value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={tc("namePlaceholder")} />
            </Field>
            <Field label={tc("nameEn")}>
              <input className={c.input} value={form.nameEn} maxLength={60} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} placeholder={tc("nameEnPlaceholder")} />
            </Field>
            <Field label={tc("costType")}>
              <select className={c.input} value={form.costType} onChange={(e) => setForm({ ...form, costType: e.target.value as ExpenseCostType })}>
                <option value="variable">{tc("variable")}</option>
                <option value="fijo">{tc("fixed")}</option>
              </select>
            </Field>
            <Field label={tc("color")}>
              <input type="color" aria-label={tc("color")} className={`h-[38px] w-full cursor-pointer rounded-lg border ${c.border}`} value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            </Field>
            <div className="flex gap-2">
              <button type="button" className={c.primary} disabled={busy || !form.name.trim()} onClick={submit}>
                {form.id ? t("expenses.save") : tc("add")}
              </button>
              {form.id && (
                <button type="button" className={c.secondary} onClick={() => setForm(emptyForm)}>
                  {t("expenses.cancel")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`${c.card} overflow-x-auto p-0`}>
        {!rows ? (
          <div className="p-4">
            <Spinner />
          </div>
        ) : (
          <table className="w-full min-w-[760px] text-sm" aria-label={tc("title")}>
            <thead>
              <tr className={`text-left text-xs ${c.muted}`}>
                <th className="px-4 py-2">{tc("name")}</th>
                <th className="px-2 py-2">{tc("costType")}</th>
                <th className="px-2 py-2">{tc("usage")}</th>
                <th className="px-2 py-2">{tc("state")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-t ${c.border} ${c.text} ${r.archived ? "opacity-60" : ""}`} data-testid={`category-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-2 font-medium">
                      <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: r.color }} />
                      {r.label[lang(i18n.language)]}
                    </span>
                    {r.label.en !== r.label.es && <p className={`pl-5 text-xs ${c.muted}`}>{lang(i18n.language) === "en" ? r.label.es : r.label.en}</p>}
                  </td>
                  <td className="px-2 py-2.5">
                    <Chip className={r.costType === "fijo" ? "bg-indigo-500/15 text-indigo-600" : "bg-sky-500/15 text-sky-600"}>{tc(r.costType === "fijo" ? "fixed" : "variable")}</Chip>
                  </td>
                  <td className={`px-2 py-2.5 text-xs ${c.muted}`}>{usageText(r)}</td>
                  <td className="px-2 py-2.5">
                    <span className="inline-flex flex-wrap gap-1">
                      {r.archived && <Chip className="bg-amber-500/15 text-amber-600">{tc("stateArchived")}</Chip>}
                      {r.builtin ? (
                        <Chip className="bg-slate-500/15 text-slate-500">{tc(r.protected ? "stateProtected" : "stateSystem")}</Chip>
                      ) : (
                        <Chip className="bg-teal-500/15 text-teal-600">{tc("stateCustom")}</Chip>
                      )}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right">
                    {canEdit && (
                      <>
                        <button type="button" aria-label={`edit-${r.id}`} title={t("expenses.list.edit")} className="mr-3 text-teal-600" disabled={busy} onClick={() => edit(r)}>
                          <i className="ri-edit-line" />
                        </button>
                        {r.archived ? (
                          <button type="button" aria-label={`restore-${r.id}`} title={tc("restore")} className="text-teal-600" disabled={busy} onClick={() => setArchived(r, false)}>
                            <i className="ri-inbox-unarchive-line" />
                          </button>
                        ) : r.protected ? (
                          <span className={`text-xs ${c.muted}`} title={tc("protectedHint")}>
                            <i className="ri-lock-line" />
                          </span>
                        ) : !r.builtin && !inUse(r) ? (
                          <button type="button" aria-label={`delete-${r.id}`} title={tc("delete")} className="text-red-500" disabled={busy} onClick={() => remove(r)}>
                            <i className="ri-delete-bin-line" />
                          </button>
                        ) : (
                          <button type="button" aria-label={`archive-${r.id}`} title={tc("archive")} className="text-amber-600" disabled={busy} onClick={() => setArchived(r, true)}>
                            <i className="ri-inbox-archive-line" />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Notice tone="info">{tc("rules")}</Notice>
    </Page>
  );
}
